import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
// @ts-ignore leaflet-draw has no TS types in this project
import "leaflet-draw";
// @ts-ignore leaflet-draw CSS
import "leaflet-draw/dist/leaflet.draw.css";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import StarBorderRoundedIcon from "@mui/icons-material/StarBorderRounded";
import CenterFocusStrongOutlinedIcon from "@mui/icons-material/CenterFocusStrongOutlined";
import LayersClearOutlinedIcon from "@mui/icons-material/LayersClearOutlined";
import FormatListNumberedRoundedIcon from "@mui/icons-material/FormatListNumberedRounded";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { prefersReducedMotion } from "../design/transitions";
import { CanvasContext, ICanvasComponent } from "../canvasContext.tsx";
import { GetTitlesCallNoStore } from "../../redux/api/title.api";
import {
  SYSTEM_CATALOGUE_OPTIONS,
  apiTypeToSourceLabel,
  catalogueOptionTitle,
  readFavoriteCatalogueKey,
  writeFavoriteCatalogueKey,
} from "../../utils/courseCatalog";

export type SlideOption = {
  label: string;
  tileUrl: string;
  thumbnailUrl: string;
  courseKey: string;
};

/** Prefer localization for active lang (API already filters; fallback like the app). */
function getTitleLabel(item: any, field = "title", lang = "en") {
  const list = item?.titleLocalizations?.filter((e: any) => e.field === field) || [];
  const match =
    list.find((e: any) => e.language === lang) ||
    list.find((e: any) => e.language === "en") ||
    list[0];
  return match?.value || item?.title || "";
}

/** Convert circle (center + radius in m) to GeoJSON Polygon so it survives save/load (no reliance on properties). */
function circleToPolygonFeature(lat: number, lng: number, radiusMeters: number): any {
  const points: [number, number][] = [];
  const n = 32;
  const latRad = (lat * Math.PI) / 180;
  const metersPerDegLat = 111320;
  const metersPerDegLng = 111320 * Math.cos(latRad);
  for (let i = 0; i <= n; i++) {
    const angle = (2 * Math.PI * i) / n;
    const dLat = (radiusMeters / metersPerDegLat) * Math.cos(angle);
    const dLng = (radiusMeters / metersPerDegLng) * Math.sin(angle);
    points.push([lng + dLng, lat + dLat]);
  }
  return {
    type: "Feature",
    geometry: { type: "Polygon", coordinates: [points] },
    properties: {},
  };
}

/** Normal map-pin icon (avoids broken default Leaflet marker images in bundled apps) */
function createPinIcon(): L.DivIcon {
  return L.divIcon({
    className: "slide-pin-icon",
    html: `<span class="slide-pin-icon-inner"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 40" width="24" height="40"><path fill="#3388ff" stroke="#fff" stroke-width="2" d="M12 0C5.4 0 0 5.4 0 12c0 9 12 28 12 28s12-19 12-28C24 5.4 18.6 0 12 0z"/><circle fill="#fff" cx="12" cy="12" r="4"/></svg></span>`,
    iconSize: [24, 40],
    iconAnchor: [12, 40],
  });
}

function buildSlideEntries(
  titles: any[],
  sourceLabel: string,
  courseKey: string,
  lang: string
): SlideOption[] {
  const entries: SlideOption[] = [];
  (titles || []).forEach((tile) => {
    const tileLabel = getTitleLabel(tile, "title", lang);
    (tile.subtitles || []).forEach((sub: any) => {
      const slide = sub?.slides?.[0];
      if (!slide?.id) return;
      const slideLabel = getTitleLabel(sub, "title", lang);
      const file = slide?.Files?.[0] || slide?.files?.[0];
      const baseUrl = file?.url;
      if (!baseUrl) return;
      const tileUrl = `${baseUrl}/{z}/{x}/{y}.png`;
      const thumbnailUrl = `${baseUrl}/0/0/0.png`;
      entries.push({
        label: `${sourceLabel}: ${tileLabel} / ${slideLabel}`,
        tileUrl,
        thumbnailUrl,
        courseKey,
      });
    });
  });
  return entries;
}

const COURSE_API_LOAD = [
  { key: "histology", apiType: "his" },
  { key: "embryology", apiType: "emb" },
  { key: "anatomy", apiType: "anatomy" },
] as const;

const SlideElement = (props: ICanvasComponent) => {
  const {
    content,
    id,
    dimension,
    slideZoom = 0,
    isPlayMode,
    slideAnnotations,
    slideCenter,
    slideFocus,
  } = props;
  const annotationReveal = (props as any).annotationReveal as string | undefined;
  const { actions, playback } = useContext(CanvasContext);
  const { t } = useTranslation();
  const lang = useSelector((state: any) => state.general?.lang || "en");
  const focusStops = Array.isArray(slideFocus) ? slideFocus : [];
  const savedCenter: [number, number] =
    Array.isArray(slideCenter) &&
    slideCenter.length === 2 &&
    slideCenter.every((n) => Number.isFinite(Number(n)))
      ? [Number(slideCenter[0]), Number(slideCenter[1])]
      : [0, 0];
  const viewRef = useRef({ center: savedCenter, zoom: slideZoom });
  viewRef.current = { center: savedCenter, zoom: slideZoom };
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const drawnItemsRef = useRef<L.FeatureGroup | null>(null);
  const [annotationsVisible, setAnnotationsVisible] = useState(true);
  const lastPersistedAnnotationsRef = useRef<any>(null);
  const actionsRef = useRef(actions);
  actionsRef.current = actions;
  const [slideOptions, setSlideOptions] = useState<SlideOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState<SlideOption | null>(null);
  const [favoriteCatalogue, setFavoriteCatalogue] = useState(() =>
    readFavoriteCatalogueKey("histology")
  );
  const [selectedCourseKey, setSelectedCourseKey] = useState(() =>
    readFavoriteCatalogueKey("histology")
  );

  useEffect(() => {
    if (isPlayMode) return;
    let cancelled = false;
    setLoading(true);
    setSlideOptions([]);
    Promise.all(
      COURSE_API_LOAD.map(({ apiType }) => GetTitlesCallNoStore(lang, apiType, false))
    )
      .then((results) => {
        if (cancelled) return;
        const next: SlideOption[] = [];
        COURSE_API_LOAD.forEach(({ key, apiType }, i) => {
          const list = Array.isArray(results[i]) ? results[i] : [];
          next.push(
            ...buildSlideEntries(list, apiTypeToSourceLabel(apiType, t), key, lang)
          );
        });
        setSlideOptions(next);
      })
      .catch(() => {
        if (!cancelled) setSlideOptions([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isPlayMode, lang, t]);

  const filteredSlideOptions = useMemo(() => {
    if (selectedCourseKey === "all") return slideOptions;
    return slideOptions.filter((o) => o.courseKey === selectedCourseKey);
  }, [slideOptions, selectedCourseKey]);

  useEffect(() => {
    if (!selectedOption) return;
    const stillVisible = filteredSlideOptions.some(
      (o) => o.tileUrl === selectedOption.tileUrl
    );
    if (!stillVisible) setSelectedOption(null);
  }, [filteredSlideOptions, selectedOption]);

  const setFavoriteCourse = (key: string) => {
    const next = writeFavoriteCatalogueKey(key);
    setFavoriteCatalogue(next);
    setSelectedCourseKey(next);
  };

  useEffect(() => {
    if (!containerRef.current || !content) return;
    const tileUrl = typeof content === "string" ? content : (content as any)?.url;
    if (!tileUrl || !tileUrl.includes("{z}")) return;

    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
    }

    const initialZoom =
      typeof slideZoom === "number" && slideZoom >= 0
        ? Math.min(4, Math.max(0, slideZoom))
        : 0;
    const map = L.map(containerRef.current, {
      center: viewRef.current.center,
      zoom: initialZoom,
      noWrap: true,
      zoomControl: false,
      maxBounds: [
        [-90, -180],
        [90, 180],
      ],
      maxBoundsViscosity: 1,
    });
    L.control.zoom({ position: "bottomright" }).addTo(map);
    L.tileLayer(tileUrl, {
      maxZoom: 4,
      minZoom: 0,
      noWrap: true,
    }).addTo(map);

    // Feature group for all annotations (pins, lines, circles, etc.)
    const drawnItems = new L.FeatureGroup();
    drawnItems.addTo(map);
    drawnItemsRef.current = drawnItems;

    // Only show draw controls in edit mode (not when playing the presentation)
    if (!isPlayMode) {
      const pinIcon = createPinIcon();
      // @ts-ignore leaflet-draw types
      const drawControl = new (L as any).Control.Draw({
        position: "bottomleft",
        draw: {
          marker: { icon: pinIcon },
          polyline: true,
          circle: false,
          rectangle: false,
          polygon: false,
          circlemarker: false,
        },
        edit: {
          featureGroup: drawnItems,
        },
      });
      map.addControl(drawControl);

      const persistAnnotations = () => {
        if (!id) return;
        const group = drawnItemsRef.current;
        if (!group) return;
        const features: any[] = [];
        group.eachLayer((layer: any) => {
          if (layer instanceof L.Circle && !(layer instanceof L.CircleMarker)) {
            const latlng = layer.getLatLng();
            const radius = layer.getRadius();
            features.push(circleToPolygonFeature(latlng.lat, latlng.lng, radius));
          } else {
            features.push(layer.toGeoJSON());
          }
        });
        const geojson = { type: "FeatureCollection", features };
        lastPersistedAnnotationsRef.current = geojson;
        actionsRef.current?.updateCanvasData({ id, slideAnnotations: geojson as any });
        actionsRef.current?.saveNow?.();
      };
      // @ts-ignore use leaflet-draw events
      map.on((L as any).Draw.Event.CREATED, (e: any) => {
        const layer = e.layer;
        drawnItems.addLayer(layer);
        // Defer persist so the circle (or other shape) stays visible and isn’t cleared by the sync effect
        setTimeout(() => persistAnnotations(), 0);
      });
      map.on((L as any).Draw.Event.EDITED, persistAnnotations);
      map.on((L as any).Draw.Event.DELETED, persistAnnotations);
    }

    const onZoomEnd = () => {
      const zoom = map.getZoom();
      actionsRef.current?.updateCanvasData({ id, slideZoom: zoom });
      actionsRef.current?.saveNow?.();
    };
    map.on("zoomend", onZoomEnd);
    // Remember where the professor panned so the slide (and focus stops) reopen there.
    const onMoveEnd = () => {
      const c = map.getCenter();
      const next: [number, number] = [
        Math.round(c.lat * 10000) / 10000,
        Math.round(c.lng * 10000) / 10000,
      ];
      const prev = viewRef.current.center;
      if (prev[0] === next[0] && prev[1] === next[1]) return;
      actionsRef.current?.updateCanvasData({ id, slideCenter: next });
      actionsRef.current?.saveNow?.();
    };
    if (!isPlayMode) map.on("moveend", onMoveEnd);
    mapRef.current = map;

    return () => {
      map.off("zoomend", onZoomEnd);
      map.off("moveend", onMoveEnd);
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
      drawnItemsRef.current = null;
    };
  }, [content, id, isPlayMode]);

  useEffect(() => {
    lastPersistedAnnotationsRef.current = null;
  }, [content, id]);

  // When annotations change (loaded from saved canvas), render them on the map.
  // Skip re-sync when we just persisted (same ref) so drawn circles stay circles instead of being re-parsed.
  useEffect(() => {
    const map = mapRef.current;
    const drawnItems = drawnItemsRef.current;
    if (!map || !drawnItems) return;
    if (!annotationsVisible) {
      drawnItems.clearLayers();
      return;
    }
    if (slideAnnotations && slideAnnotations === lastPersistedAnnotationsRef.current) return;

    drawnItems.clearLayers();
    if (slideAnnotations) {
      const pinIcon = createPinIcon();
      L.geoJSON(slideAnnotations as any, {
        pointToLayer: (feature: any, latlng) => {
          const radius = Number(
            feature?.properties?.radius ??
              feature?.properties?.Radius ??
              feature?.properties?.radius_meters
          );
          if (!Number.isNaN(radius) && radius > 0) {
            return L.circle(latlng, { radius });
          }
          if (feature?.properties?._type === "circle" && (radius === 0 || Number.isNaN(radius))) {
            return L.circle(latlng, { radius: 50 });
          }
          return L.marker(latlng, { icon: pinIcon });
        },
      }).eachLayer((layer) => {
        drawnItems.addLayer(layer);
      });
    }
  }, [slideAnnotations, annotationsVisible]);

  useEffect(() => {
    const m = mapRef.current;
    if (!m || !containerRef.current) return;
    m.invalidateSize();
  }, [dimension?.width, dimension?.height]);

  // Presentation mode: walk through focus stops, then reveal labels one by one.
  const annotationFeatures: any[] = Array.isArray((slideAnnotations as any)?.features)
    ? (slideAnnotations as any).features
    : [];
  const progressiveLabels = annotationReveal === "progressive" && annotationFeatures.length > 0;
  const focusStart = id ? playback?.focusStart?.get(id) : undefined;
  const localStep =
    !isPlayMode || focusStart == null
      ? null
      : playback?.revealAll
        ? Number.POSITIVE_INFINITY
        : playback?.active
          ? (playback.step ?? 0) - focusStart
          : -1;

  useEffect(() => {
    if (!isPlayMode || localStep == null) return;
    const map = mapRef.current;
    if (!map) return;
    const stopIndex = Math.min(localStep, focusStops.length - 1);
    const target =
      stopIndex >= 0 && focusStops[stopIndex]
        ? { center: focusStops[stopIndex].center, zoom: focusStops[stopIndex].zoom }
        : viewRef.current;
    const animate = Boolean(playback?.active) && !playback?.instant && !prefersReducedMotion();
    try {
      if (animate) {
        map.flyTo(target.center as L.LatLngExpression, target.zoom, { duration: 1.4, easeLinearity: 0.2 });
      } else {
        map.setView(target.center as L.LatLngExpression, target.zoom, { animate: false });
      }
    } catch (error) {
      /* map not ready */
    }
    containerRef.current?.classList.toggle("cine-pins-pulse", stopIndex >= 0 && focusStops.length > 0);
  }, [isPlayMode, localStep, playback?.active, playback?.token]);

  useEffect(() => {
    if (!isPlayMode || !progressiveLabels) return;
    const group = drawnItemsRef.current;
    if (!group) return;
    const shown =
      localStep == null ? annotationFeatures.length : Math.max(0, localStep - focusStops.length + 1);
    let i = 0;
    group.eachLayer((layer: any) => {
      const visible = i < shown;
      const el: HTMLElement | SVGElement | undefined = layer.getElement?.() || layer._icon || layer._path;
      if (el) {
        const wasHidden = el.style.opacity === "0";
        el.style.transition = "opacity 420ms ease, filter 420ms ease";
        el.style.opacity = visible ? "1" : "0";
        el.style.filter = visible && wasHidden ? "drop-shadow(0 0 8px rgba(239,68,68,0.9))" : "";
        if (visible && wasHidden) window.setTimeout(() => { el.style.filter = ""; }, 900);
      }
      i += 1;
    });
  }, [isPlayMode, progressiveLabels, localStep, slideAnnotations]);

  const addFocusStop = () => {
    const map = mapRef.current;
    if (!map || !id) return;
    const c = map.getCenter();
    const stop = {
      id: `focus_${Date.now()}`,
      label: `Focus ${focusStops.length + 1}`,
      center: [Math.round(c.lat * 10000) / 10000, Math.round(c.lng * 10000) / 10000] as [number, number],
      zoom: map.getZoom(),
    };
    actions?.updateCanvasData({ id, slideFocus: [...focusStops, stop] });
    actions?.saveNow?.();
  };

  const clearFocusStops = () => {
    if (!id) return;
    actions?.updateCanvasData({ id, slideFocus: [] });
    actions?.saveNow?.();
  };

  const toggleProgressiveLabels = () => {
    if (!id) return;
    actions?.updateCanvasData({
      id,
      annotationReveal: annotationReveal === "progressive" ? "all" : "progressive",
    } as any);
    actions?.saveNow?.();
  };

  const handleSelectSlide = (option: SlideOption | null) => {
    if (option) {
      actions?.updateCanvasData({ id, content: option.tileUrl });
      setSelectedOption(option);
      actions?.saveNow?.();
    }
  };

  const handleUrlBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const url = e.target?.value?.trim();
    if (url) {
      actions?.updateCanvasData({ id, content: url });
      actions?.saveNow?.();
    }
  };

  if (!content) {
    if (isPlayMode) {
      return (
        <div
          style={{
            width: "100%",
            height: "100%",
            background: "#e5e7eb",
            borderRadius: 6,
          }}
        />
      );
    }
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          background: "#e8e8e8",
          padding: 8,
          overflow: "auto",
        }}
      >
        <span style={{ fontSize: 12, marginBottom: 6, display: "block" }}>
          {t("pages.presentationMaker.searchSelectSlide")}
        </span>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
          <FormControl size="small" fullWidth>
            <InputLabel id="slide-course-label">
              {t("pages.presentationMaker.course")}
            </InputLabel>
            <Select
              labelId="slide-course-label"
              label={t("pages.presentationMaker.course")}
              value={selectedCourseKey}
              onChange={(e) => setSelectedCourseKey(String(e.target.value))}
              renderValue={(selected) => {
                const opt = SYSTEM_CATALOGUE_OPTIONS.find((o) => o.key === selected);
                return catalogueOptionTitle(opt, t) || String(selected);
              }}
            >
              {SYSTEM_CATALOGUE_OPTIONS.map((opt) => (
                <MenuItem key={opt.key} value={opt.key}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      width: "100%",
                    }}
                  >
                    <Tooltip
                      title={
                        favoriteCatalogue === opt.key
                          ? t("pages.presentationMaker.defaultCourse")
                          : t("pages.presentationMaker.setDefaultCourse")
                      }
                    >
                      <IconButton
                        size="small"
                        edge="start"
                        aria-label={
                          favoriteCatalogue === opt.key
                            ? t("pages.presentationMaker.defaultCourse")
                            : t("pages.presentationMaker.setDefaultCourse")
                        }
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setFavoriteCourse(opt.key);
                        }}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        sx={{ p: 0.25 }}
                      >
                        {favoriteCatalogue === opt.key ? (
                          <StarRoundedIcon
                            sx={{ fontSize: 18, color: "warning.main" }}
                          />
                        ) : (
                          <StarBorderRoundedIcon
                            sx={{ fontSize: 18, opacity: 0.45 }}
                          />
                        )}
                      </IconButton>
                    </Tooltip>
                    {catalogueOptionTitle(opt, t)}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip
            title={
              favoriteCatalogue === selectedCourseKey
                ? t("pages.presentationMaker.defaultCourse")
                : t("pages.presentationMaker.setCourseAsDefault")
            }
          >
            <span>
              <IconButton
                size="small"
                color={
                  favoriteCatalogue === selectedCourseKey ? "warning" : "default"
                }
                onClick={() => setFavoriteCourse(selectedCourseKey)}
                aria-label={t("pages.presentationMaker.setCourseAsDefault")}
              >
                {favoriteCatalogue === selectedCourseKey ? (
                  <StarRoundedIcon />
                ) : (
                  <StarBorderRoundedIcon />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        <Autocomplete
          size="small"
          fullWidth
          loading={loading}
          options={filteredSlideOptions}
          value={selectedOption}
          onChange={(_, value) => handleSelectSlide(value)}
          getOptionLabel={(opt) => opt?.label ?? ""}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={t("pages.presentationMaker.searchByName")}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <InputAdornment position="start">🔍</InputAdornment>
                    {params.InputProps.startAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, opt) => (
            <li {...props} key={`${opt.label}-${opt.tileUrl}`}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  component="img"
                  src={opt.thumbnailUrl}
                  alt=""
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = "none";
                  }}
                  sx={{
                    width: 40,
                    height: 40,
                    objectFit: "cover",
                    borderRadius: 1,
                    flexShrink: 0,
                    bgcolor: "grey.200",
                  }}
                />
                <span style={{ fontSize: 13 }}>{opt.label}</span>
              </Box>
            </li>
          )}
        />
        <span style={{ fontSize: 11, marginTop: 8, marginBottom: 4 }}>
          {t("pages.presentationMaker.orPasteTileUrl")}
        </span>
        <input
          type="url"
          placeholder=".../{z}/{x}/{y}.png"
          style={{ width: "100%", padding: 6, fontSize: 12, boxSizing: "border-box" }}
          onBlur={handleUrlBlur}
        />
      </div>
    );
  }

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        minHeight: 80,
      }}
    >
      <div
        ref={containerRef}
        style={{
          width: "100%",
          height: "100%",
          minHeight: 80,
        }}
      />
      {!isPlayMode && content && (
        <Box
          sx={{
            position: "absolute",
            bottom: 160,
            left: 12,
            zIndex: 1200,
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
          }}
        >
          <IconButton
            size="small"
            onClick={() => setAnnotationsVisible((v) => !v)}
            aria-label={
              annotationsVisible
                ? t("pages.presentationMaker.hideAnnotations")
                : t("pages.presentationMaker.showAnnotations")
            }
            sx={{
              width: 30,
              height: 30,
              bgcolor: "background.paper",
              border: "1px solid",
              borderColor: "divider",
              boxShadow: 2,
              "&:hover": { bgcolor: "action.hover" },
              "& .MuiSvgIcon-root": { fontSize: 18 },
            }}
          >
            {annotationsVisible ? (
              <VisibilityOffIcon fontSize="small" />
            ) : (
              <VisibilityIcon fontSize="small" />
            )}
          </IconButton>
          <Tooltip
            title={t("pages.presentationMaker.addFocusStop", {
              count: focusStops.length,
            })}
            placement="right"
          >
            <IconButton
              size="small"
              onClick={addFocusStop}
              aria-label={t("pages.presentationMaker.addFocusStop", {
                count: focusStops.length,
              })}
              sx={{
                width: 30,
                height: 30,
                bgcolor: focusStops.length ? "#0ea5e9" : "background.paper",
                color: focusStops.length ? "#fff" : "inherit",
                border: "1px solid",
                borderColor: "divider",
                boxShadow: 2,
                fontSize: 11,
                "&:hover": { bgcolor: focusStops.length ? "#0284c7" : "action.hover" },
                "& .MuiSvgIcon-root": { fontSize: 18 },
              }}
            >
              <CenterFocusStrongOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {focusStops.length > 0 ? (
            <Tooltip
              title={t("pages.presentationMaker.clearFocusStops")}
              placement="right"
            >
              <IconButton
                size="small"
                onClick={clearFocusStops}
                aria-label={t("pages.presentationMaker.clearFocusStops")}
                sx={{
                  width: 30,
                  height: 30,
                  bgcolor: "background.paper",
                  border: "1px solid",
                  borderColor: "divider",
                  boxShadow: 2,
                  "&:hover": { bgcolor: "action.hover" },
                  "& .MuiSvgIcon-root": { fontSize: 18 },
                }}
              >
                <LayersClearOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
          {annotationFeatures.length > 0 ? (
            <Tooltip
              title={
                annotationReveal === "progressive"
                  ? t("pages.presentationMaker.progressiveLabelsOn")
                  : t("pages.presentationMaker.progressiveLabelsOff")
              }
              placement="right"
            >
              <IconButton
                size="small"
                onClick={toggleProgressiveLabels}
                aria-label={t("pages.presentationMaker.progressiveLabelsOff")}
                aria-pressed={annotationReveal === "progressive"}
                sx={{
                  width: 30,
                  height: 30,
                  bgcolor: annotationReveal === "progressive" ? "#0ea5e9" : "background.paper",
                  color: annotationReveal === "progressive" ? "#fff" : "inherit",
                  border: "1px solid",
                  borderColor: "divider",
                  boxShadow: 2,
                  "&:hover": { bgcolor: annotationReveal === "progressive" ? "#0284c7" : "action.hover" },
                  "& .MuiSvgIcon-root": { fontSize: 18 },
                }}
              >
                <FormatListNumberedRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
        </Box>
      )}
    </div>
  );
};

export default SlideElement;
